import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import type { KitTypeCategory } from '../../actions/getKitTypeCategories';
import type { KitType } from '../../actions/getKitTypes';
import { sendAdminRequest } from '../../api/websocketApi';
import CustomButton from '../../components/Button';
import EditKitTypeForm from '../../components/EditKitTypeForm';
import useStore from '../../store/store';

const AdminTestsPage = () => {
    const setTitle = useStore((state) => state.setTitle);
    const [kitTypes, setKitTypes] = useState<KitType[]>([]);
    const kitTypeCategories = useStore((state) => state.kitTypeCategories);
    const setFormError = useStore((state) => state.setFormError);
    const [editKitTypeDialogOpen, setEditKitTypeDialogOpen] = useState(false);
    const [kitType, setKitType] = useState<KitType | null>(null);
    const [kitTypeUpdated, setKitTypeUpdated] = useState(false);
    const [isAddMode, setIsAddMode] = useState(false);
    const classes = useStyles({});

    const handleNewTestTypeClick = () => {
        setIsAddMode(true);
        setKitType(null);
        setEditKitTypeDialogOpen(true);
    };

    const handleEditKitTypeClick = (kitType: KitType) => {
        setKitType(kitType);
        setEditKitTypeDialogOpen(true);
    };

    const handleKitTypeDialogClose = () => {
        setEditKitTypeDialogOpen(false);
        setIsAddMode(false);
        setKitType(null);
    };

    const handleKitTypeUpdated = () => setKitTypeUpdated(true);

    useEffect(() => {
        setTitle('Admin - Tests');
    }, [setTitle]);

    useEffect(() => {
        interface APIResponse {
            success: boolean;
            kitTypes?: KitType[];
            errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
            errDetails?: string;
        }

        sendAdminRequest(
            {
                endpoint: '/kit-type/get-all',
            },
            (response: APIResponse) => {
                if (response.success && Array.isArray(response.kitTypes)) {
                    setKitTypes(response.kitTypes);
                } else {
                    setFormError(response.errCode || 'Unknown error');
                }
                setKitTypeUpdated(false);
            }
        );
    }, [setFormError, kitTypeUpdated]);

    return (
        <>
            <div className={classes.root}>
                <div className={classes.buttons}>
                    <CustomButton
                        icon={<AddIcon />}
                        onClick={handleNewTestTypeClick}
                    >
                        New Test Type
                    </CustomButton>
                </div>
                <TableContainer style={{ borderRadius: '12px' }}>
                    <Table style={{ minWidth: 650 }} aria-label="test types">
                        <TableHead>
                            <TableRow>
                                <TableCell width="10%">
                                    <b>Type</b>
                                </TableCell>
                                <TableCell width="5%">
                                    <b>ID</b>
                                </TableCell>
                                <TableCell>
                                    <b>Description</b>
                                </TableCell>
                                <TableCell width="5%">
                                    <b>Category</b>
                                </TableCell>
                                <TableCell width="5">
                                    <b>Turnaround Time</b>
                                </TableCell>
                                <TableCell>
                                    <b>Required Sample</b>
                                </TableCell>
                                <TableCell width="5%">
                                    <b>Transport Temperature</b>
                                </TableCell>
                                <TableCell>
                                    <b>Provided Results</b>
                                </TableCell>
                                <TableCell width="5%">
                                    <b>Available</b>
                                </TableCell>
                                <TableCell width="5%">
                                    <b>Actions</b>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {kitTypes?.map((kitType: KitType) => (
                                <TableRow
                                    key={kitType.KitTypeId}
                                    className={classes.noBorderLastRow}
                                >
                                    <TableCell>{kitType.Type}</TableCell>
                                    <TableCell>
                                        {kitType.KitTypeCatalogueId ?? 'N/A'}
                                    </TableCell>
                                    <TableCell>{kitType.Description}</TableCell>
                                    <TableCell>
                                        {kitTypeCategories.find(
                                            (category: KitTypeCategory) =>
                                                category.KitTypeCategoryId ===
                                                kitType.KitTypeCategoryId
                                        )?.Name || ''}
                                    </TableCell>
                                    <TableCell>
                                        {kitType.TurnaroundTime}
                                    </TableCell>
                                    <TableCell>
                                        {kitType.RequiredSample}
                                    </TableCell>
                                    <TableCell>
                                        {kitType.TransportTempo}
                                    </TableCell>
                                    <TableCell>
                                        {kitType.ProvidedReports}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            disabled
                                            checked={kitType.Available === 1}
                                            classes={{
                                                root: classes.checkboxRoot,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            onClick={() =>
                                                handleEditKitTypeClick(kitType)
                                            }
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <EditKitTypeForm
                open={editKitTypeDialogOpen}
                onClose={handleKitTypeDialogClose}
                kitType={kitType || undefined}
                onKitTypeUpdated={handleKitTypeUpdated}
                isAddMode={isAddMode}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        padding: '40px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '32px',
        flex: '1 0 0',
        alignSelf: 'stretch',
        background: theme.palette.primary.lighter,
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
    },
    noBorderLastRow: {
        '&:last-child td, &:last-child th': {
            border: 0,
        },
    },
    checkboxRoot: {
        color: theme.palette.text.secondary,
        '&.Mui-checked': {
            color: theme.palette.primary.main,
        },
    },
}));

export default AdminTestsPage;
